import React, { useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'

import { SelectDropdown, type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { Modal } from '../../../../../components/Modal/Modal'
import { Button } from '../../../../../components/Button/Button'

import { usePrivateMeetingsEnabled } from '../../../../../common/usePrivateMeetingsEnabled'

import { useUpsertManualParticipantMutation } from '../../../../../store/media/media'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../../../store/user/user'
import { useAppSelector, useAppDispatch } from '../../../../../store/hooks'
import { showToast } from '../../../../../store/meetingSummary/display'

import { type User } from '../../../../../models/User'
import { ManualParticipantAddedReason } from '../../../../../models/ManualParticipant'

import { TrackEvent, useTracking } from '../../common/tracking'
import { mapUserToSelectOption } from '../../MeetingPreparation/Participants/utils'
import { type UsersConsent } from '../hooks/users'

import { getManualParticipantReasonOptions, isManualParticipantReasonValid, shouldShowManualParticipantConsentWarning } from './utils'
import { AllocationUserTagDropdownItemOption } from './AllocationUserTagDropdownItemOption'

export interface AddMissingParticipantModalProps {
  show: boolean
  roomId: string
  sessionId: string
  meetingUsers: User[]
  usersConsent: UsersConsent | null
  consentTrackingEnabled: boolean
  canManagePrivateTeammateAccess: boolean
  isPrivate: boolean
  onCancel: () => void
  onSuccess: () => void
}

interface AddMissingParticipantModalFormValues {
  assignments: AddMissingParticipantAssignment[]
}

interface AddMissingParticipantAssignment {
  selectedUser: User | null
  reason: ManualParticipantAddedReason | null
  otherExplanation: string
}

type ValidAddMissingParticipantAssignment = AddMissingParticipantAssignment & {
  selectedUser: User
  reason: ManualParticipantAddedReason
}

const getEmptyAssignment = (): AddMissingParticipantAssignment => ({
  selectedUser: null,
  reason: null,
  otherExplanation: '',
})

const isValidAssignment = (
  assignment: AddMissingParticipantAssignment,
  canManagePrivateTeammateAccess: boolean,
): assignment is ValidAddMissingParticipantAssignment => {
  return (
    !!assignment.selectedUser &&
    (canManagePrivateTeammateAccess || assignment.selectedUser.type !== 'managed') &&
    isManualParticipantReasonValid(assignment.reason, assignment.otherExplanation)
  )
}

export const AddParticipantModal: React.FC<AddMissingParticipantModalProps> = ({
  show,
  roomId,
  sessionId,
  meetingUsers,
  usersConsent,
  consentTrackingEnabled,
  canManagePrivateTeammateAccess,
  isPrivate,
  onCancel,
  onSuccess,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()

  const form = useForm<AddMissingParticipantModalFormValues>({
    defaultValues: { assignments: [getEmptyAssignment()] },
  })
  const { control } = form

  const assignments = useWatch({ control, name: 'assignments' })

  const [triggerUpsertManualParticipant, { isLoading: upserting }] = useUpsertManualParticipantMutation()

  const addParticipant = tracked(
    async ({ selectedUser, reason, otherExplanation }: ValidAddMissingParticipantAssignment) => {
      await triggerUpsertManualParticipant({
        accountId,
        roomId,
        sessionId,
        body: {
          userId: selectedUser.id,
          addedReason: reason,
          addedReasonExplanation: reason === ManualParticipantAddedReason.OTHER ? otherExplanation.trim() : undefined,
        },
      }).unwrap()
    },
    { eventName: TrackEvent.PARTICIPANT_ADDED },
    {
      eventDetailsExtractor: ({ selectedUser, reason, otherExplanation }: ValidAddMissingParticipantAssignment) => ({
        userId: selectedUser.id,
        source: 'past',
        type: selectedUser.type === 'managed' ? 'teammate' : 'client',
        roomId,
        sessionId,
        reason,
        ...(otherExplanation.trim() && { reasonExplanation: otherExplanation.trim() }),
      }),
    },
  )

  const onSubmit = async (formData: AddMissingParticipantModalFormValues) => {
    const validAssignments = formData.assignments.filter((assignment) => isValidAssignment(assignment, canManagePrivateTeammateAccess))

    if (!validAssignments.length) {
      return
    }

    await Promise.all(validAssignments.map((assignment) => addParticipant(assignment)))
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'New participants added' }))
    onSuccess()
  }

  const meetingIsPrivate = privateMeetingsEnabled && isPrivate

  const selectedUsers = assignments?.flatMap((assignment) => (assignment.selectedUser ? [assignment.selectedUser] : [])) ?? []

  const hasDuplicateUsers = new Set(selectedUsers.map((user) => user.id)).size !== selectedUsers.length

  const isFormValid =
    !!assignments?.length && !hasDuplicateUsers && assignments.every((assignment) => isValidAssignment(assignment, canManagePrivateTeammateAccess))

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size='lg'
      header={<h4>Add participant</h4>}
      body={
        <FormProvider {...form}>
          <AddMissingParticipantModalBody
            meetingUsers={meetingUsers}
            usersConsent={usersConsent}
            consentTrackingEnabled={consentTrackingEnabled}
            canManagePrivateTeammateAccess={canManagePrivateTeammateAccess}
            meetingIsPrivate={meetingIsPrivate}
          />
        </FormProvider>
      }
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={form.handleSubmit(onSubmit)}
      footerPrimaryActionTitle='Add'
      footerPrimaryActionLoading={upserting}
      footerPrimaryActionDisabled={!isFormValid}
      onFooterSecondaryAction={onCancel}
      centered={true}
    />
  )
}

interface AddMissingParticipantModalBodyProps {
  meetingUsers: User[]
  usersConsent: UsersConsent | null
  consentTrackingEnabled: boolean
  canManagePrivateTeammateAccess: boolean
  meetingIsPrivate: boolean
}

const AddMissingParticipantModalBody: React.FC<AddMissingParticipantModalBodyProps> = ({
  meetingUsers,
  usersConsent,
  consentTrackingEnabled,
  canManagePrivateTeammateAccess,
  meetingIsPrivate,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { control, setValue } = useFormContext<AddMissingParticipantModalFormValues>()
  const { fields: assignments, append, remove } = useFieldArray({ control, name: 'assignments' })

  const selectedAssignments = useWatch({ control, name: 'assignments' })

  const [searchInput, setSearchInput] = useState<string>('')

  const { data: usersResponse } = useGetUsersQuery({
    accountId,
    name: searchInput,
    identityTypes: canManagePrivateTeammateAccess ? ['external', 'idp'] : ['external'],
    pageRequest: { page: 0, size: 50 },
  })

  const userOptions = useMemo(
    () => usersResponse?.data.filter((user) => !meetingUsers.some((meetingUser) => meetingUser.id === user.id)).map(mapUserToSelectOption) ?? [],
    [meetingUsers, usersResponse],
  )

  const selectedUsers = useMemo(
    () => selectedAssignments?.flatMap((assignment) => (assignment?.selectedUser ? [assignment.selectedUser] : [])) ?? [],
    [selectedAssignments],
  )

  const reasonOptions = useMemo(() => getManualParticipantReasonOptions('Other (requires explanation)'), [])

  const selectedUsersContainManagedUser = useMemo(() => selectedUsers.some((user) => user.type === 'managed'), [selectedUsers])

  const hasAnyManualParticipantConsentWarning = useMemo(() => {
    return selectedAssignments.some((assignment) =>
      shouldShowManualParticipantConsentWarning(assignment.selectedUser, usersConsent, assignment.reason, consentTrackingEnabled),
    )
  }, [selectedAssignments, usersConsent, consentTrackingEnabled])

  const getUserOptions = (assignmentIndex: number): SelectOption[] => {
    const selectedUserIds =
      selectedAssignments
        ?.filter((_, idx) => idx !== assignmentIndex)
        .flatMap((assignment) => (assignment?.selectedUser ? [assignment.selectedUser.id] : [])) ?? []

    return userOptions.filter((option) => !selectedUserIds.includes(option.id))
  }

  const handleParticipantSelected = (assignmentIndex: number, selectedOption: SelectOption) => {
    const user = usersResponse?.data.find((user) => user.id === selectedOption.id)
    if (!user) {
      return
    }

    setValue(`assignments.${assignmentIndex}.selectedUser`, user)
  }

  const handleSearchInputChange = async (newSearchInput: string) => {
    setSearchInput(newSearchInput)
  }

  const handleReasonSelected = (assignmentIndex: number, reason: ManualParticipantAddedReason) => {
    setValue(`assignments.${assignmentIndex}.reason`, reason)
    if (reason !== ManualParticipantAddedReason.OTHER) {
      setValue(`assignments.${assignmentIndex}.otherExplanation`, '')
    }
  }

  const handleAddAssignment = () => {
    append(getEmptyAssignment())
  }

  const getSelectedParticipantOption = (assignmentIndex: number): SelectOption | null => {
    const selectedUser = selectedAssignments?.[assignmentIndex]?.selectedUser
    if (!selectedUser) {
      return null
    }
    return mapUserToSelectOption(selectedUser)
  }

  const handleRemoveAssignment = (assignmentIndex: number) => {
    if (assignments.length === 1) {
      setValue(`assignments.${assignmentIndex}.selectedUser`, null)
      setValue(`assignments.${assignmentIndex}.reason`, null)
      setValue(`assignments.${assignmentIndex}.otherExplanation`, '')
      return
    }
    remove(assignmentIndex)
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='paragraph-medium'>Add clients and teammates to ensure the right contacts are associated with this meeting.</div>

      <div className='d-flex flex-column'>
        {assignments.map((assignment, index) => {
          const reason = selectedAssignments?.[index]?.reason ?? null
          const selectedReasonOption = reason ? (reasonOptions.find((option) => option.id === reason) ?? null) : null

          return (
            <div key={assignment.id} className='d-flex flex-column'>
              <div className='d-flex flex-column flex-lg-row align-items-stretch'>
                <div className='flex-grow-1 d-flex flex-column'>
                  <div className='d-flex flex-column flex-lg-row align-items-stretch'>
                    <div className='w-100 py-2 zocks-px-0.75'>
                      <SelectDropdown
                        className='py-2 zocks-px-0.75'
                        options={getUserOptions(index)}
                        initialSelectedOption={getSelectedParticipantOption(index)}
                        onOptionSelected={(selectedOption) => handleParticipantSelected(index, selectedOption)}
                        onSearchInputChange={handleSearchInputChange}
                        placeholder='Select participant'
                        clearDropdownOnOptionSelected={false}
                        DropDownItemOptionComponent={AllocationUserTagDropdownItemOption}
                        searchable
                      />
                    </div>

                    <div className='w-100 py-2 zocks-px-0.75'>
                      <SelectDropdown
                        className='py-2 zocks-px-0.75 zocks-gap-3'
                        options={reasonOptions}
                        initialSelectedOption={selectedReasonOption}
                        onOptionSelected={(selectedOption) => handleReasonSelected(index, selectedOption.id as ManualParticipantAddedReason)}
                        placeholder='Select reason'
                        clearDropdownOnOptionSelected={false}
                      />
                    </div>
                  </div>

                  {reason === ManualParticipantAddedReason.OTHER ? (
                    <div className='pb-2 pt-0 zocks-px-0.75 zocks-pe-3.5'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Explain the reason'
                        value={selectedAssignments?.[index]?.otherExplanation ?? ''}
                        onChange={(e) => setValue(`assignments.${index}.otherExplanation`, e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>

                <div className='d-flex align-items-center justify-content-center zocks-px-0.75 flex-shrink-0'>
                  <Button type='button' variant='borderless' className='p-0 zocks-text-color-secondary' onClick={() => handleRemoveAssignment(index)}>
                    <i className='bi bi-trash3' />
                  </Button>
                </div>
              </div>
              {index < assignments.length - 1 ? <div className='border-top border-secondary-subtle' /> : null}
            </div>
          )
        })}
      </div>

      <Button
        variant='link'
        className='text-decoration-none justify-content-start zocks-text-primary button-medium mt-2 align-self-start'
        onClick={handleAddAssignment}
      >
        + New participant
      </Button>

      {hasAnyManualParticipantConsentWarning ? (
        <div className='d-flex align-items-center zocks-gap-0.75 zocks-text-warning-text zocks-bg-color-warning zocks-border-color-warning-dark rounded-3 zocks-px-0.75 py-2'>
          <i className='bi bi-exclamation-triangle fs-6 ' />
          <span className='paragraph-small'>
            Adding clients who have not agreed to this meeting being transcribed and analyzed by your AI Assistant will make this meeting unavailable until all
            consent is approved.
          </span>
        </div>
      ) : null}

      {selectedUsersContainManagedUser && meetingIsPrivate ? (
        <div className='d-flex align-items-center zocks-gap-0.75 zocks-text-warning-text zocks-bg-color-warning zocks-border-color-warning-dark rounded-3 zocks-px-0.75 py-2'>
          <i className='bi bi-exclamation-triangle fs-6 ' />
          <span className='paragraph-small'>This meeting is private. Adding another team member will share it with them.</span>
        </div>
      ) : null}
    </div>
  )
}
